/**
 * Deploy API Contract
 *
 * oRPC contract for deployment freeze management.
 * GET endpoints are public (used by CD), PUT/DELETE require admin auth.
 */

import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================
// Zod Schemas
// ============================================

export const AppNameSchema = z.enum(["web", "marketing", "mobile"]);
export type AppName = z.infer<typeof AppNameSchema>;

export const FreezeStatusSchema = z.object({
  app: AppNameSchema,
  isFrozen: z.boolean(),
  pinnedVersion: z.string().nullable(),
  frozenBy: z.string().nullable(),
  frozenAt: z.coerce.date().nullable(),
  reason: z.string().nullable(),
});
export type FreezeStatus = z.infer<typeof FreezeStatusSchema>;

export const ListFreezesResponseSchema = z.object({
  freezes: z.record(AppNameSchema, FreezeStatusSchema),
});
export type ListFreezesResponse = z.infer<typeof ListFreezesResponseSchema>;

export const GetFreezeResponseSchema = z.object({
  freeze: FreezeStatusSchema,
});
export type GetFreezeResponse = z.infer<typeof GetFreezeResponseSchema>;

export const FreezeAppRequestSchema = z.object({
  app: AppNameSchema,
  pinnedVersion: z.string().min(7),
  reason: z.string().optional(),
});
export type FreezeAppRequest = z.infer<typeof FreezeAppRequestSchema>;

export const UnfreezeAppRequestSchema = z.object({
  app: AppNameSchema,
});
export type UnfreezeAppRequest = z.infer<typeof UnfreezeAppRequestSchema>;

// ============================================
// oRPC Contract Definition
// ============================================

/**
 * Deploy freeze management oRPC contract.
 * GET endpoints are public, PUT/DELETE require admin auth.
 */
export const deployContract = {
  /**
   * Get all freeze statuses.
   * Public endpoint used by CD pipeline.
   */
  listFreezes: oc
    .route({ method: "GET", path: "/deploy/freezes" })
    .output(ListFreezesResponseSchema),

  /**
   * Get freeze status for a specific app.
   * Public endpoint.
   */
  getFreeze: oc
    .route({ method: "GET", path: "/deploy/freezes/{app}" })
    .input(z.object({ app: AppNameSchema }))
    .output(GetFreezeResponseSchema),

  /**
   * Create or update a deployment freeze.
   * Admin-only.
   */
  freeze: oc
    .route({ method: "PUT", path: "/deploy/freezes/{app}" })
    .input(FreezeAppRequestSchema)
    .output(GetFreezeResponseSchema),

  /**
   * Remove a deployment freeze.
   * Admin-only.
   */
  unfreeze: oc
    .route({ method: "DELETE", path: "/deploy/freezes/{app}" })
    .input(UnfreezeAppRequestSchema)
    .output(GetFreezeResponseSchema),
};
